import type { StudentToofiSurface } from "@/types/student-toofi";
import type { ToofiProductMode } from "@/types/agentic-chat";

export type ToofiModeMeta = {
  key: ToofiProductMode;
  label: string;
  shortLabel: string;
  description: string;
  homeHint: string;
  compactHint: string;
  icon: "graduation-cap" | "brain-circuit" | "scan-search" | "calendar-range" | "pen-square";
};

export const TOOFI_MODE_ORDER: ToofiProductMode[] = ["learn", "practice", "review", "plan", "create"];

export const TOOFI_MODE_META: Record<ToofiProductMode, ToofiModeMeta> = {
  learn: {
    key: "learn",
    label: "Learn",
    shortLabel: "Learn",
    description: "Explain, simplify, and teach step by step.",
    homeHint: "Help me understand this.",
    compactHint: "Explain and simplify clearly.",
    icon: "graduation-cap",
  },
  practice: {
    key: "practice",
    label: "Practice",
    shortLabel: "Practice",
    description: "Quiz me, challenge me, and check understanding.",
    homeHint: "Test me and train me.",
    compactHint: "Quiz and train recall.",
    icon: "brain-circuit",
  },
  review: {
    key: "review",
    label: "Review",
    shortLabel: "Review",
    description: "Find weak spots, summaries, and revision priorities.",
    homeHint: "Show me what to revise.",
    compactHint: "Spot weak areas fast.",
    icon: "scan-search",
  },
  plan: {
    key: "plan",
    label: "Plan",
    shortLabel: "Plan",
    description: "Build, refine, and recover your study plan.",
    homeHint: "Help me organize my study.",
    compactHint: "Build or fix your plan.",
    icon: "calendar-range",
  },
  create: {
    key: "create",
    label: "Create",
    shortLabel: "Create",
    description: "Turn content into notes, maps, comparisons, or study assets.",
    homeHint: "Turn this into something useful.",
    compactHint: "Make notes and study assets.",
    icon: "pen-square",
  },
};

export const getToofiModeMeta = (mode: ToofiProductMode) => TOOFI_MODE_META[mode];

export const getToofiModesForSurface = (_surfaceKind: "reader" | "workspace") =>
  TOOFI_MODE_ORDER.map((mode) => TOOFI_MODE_META[mode]);

export const getToofiModeMenuLabel = (mode: ToofiProductMode) => {
  switch (mode) {
    case "learn":
      return "Learn";
    case "practice":
      return "Practice";
    case "review":
      return "Review";
    case "plan":
      return "Plan";
    case "create":
      return "Create";
    default:
      return "Mode";
  }
};

export const getDefaultToofiProductMode = ({
  surfaceKind,
  surface,
}: {
  surfaceKind: "reader" | "workspace";
  surface?: StudentToofiSurface | null;
}): ToofiProductMode => {
  if (surfaceKind === "reader") return "learn";
  switch (surface) {
    case "review":
      return "review";
    case "study_time":
    case "dashboard":
      return "plan";
    case "resource_detail":
      return "create";
    default:
      return "learn";
  }
};

export const getToofiModePlaceholder = ({
  mode,
  surfaceKind,
  hasContext,
}: {
  mode: ToofiProductMode | null;
  surfaceKind: "reader" | "workspace";
  hasContext?: boolean;
}) => {
  if (!mode) {
    return hasContext || surfaceKind === "reader"
      ? "Talk to Toofi freely about this page, this PDF, or your study"
      : "Talk to Toofi freely about your study, plans, or what you need right now";
  }
  switch (mode) {
    case "learn":
      return hasContext || surfaceKind === "reader"
        ? "Ask Toofi to explain, simplify, or teach this step by step"
        : "Ask Toofi to teach a topic clearly and step by step";
    case "practice":
      return hasContext || surfaceKind === "reader"
        ? "Ask Toofi to quiz you, test understanding, or create essay practice"
        : "Ask Toofi to quiz you or create focused practice";
    case "review":
      return "Ask Toofi what you should revise, what is weak, or what matters most next";
    case "plan":
      return "Ask Toofi to build, refine, or recover your study plan";
    case "create":
      return hasContext || surfaceKind === "reader"
        ? "Ask Toofi to turn this into notes, a comparison, or a revision map"
        : "Ask Toofi to create notes, comparisons, or study assets";
    default:
      return "Talk to Toofi about your study";
  }
};

export const getCompactToofiModePlaceholder = ({
  mode,
  surfaceKind,
  hasContext,
}: {
  mode: ToofiProductMode | null;
  surfaceKind: "reader" | "workspace";
  hasContext?: boolean;
}) => {
  if (!mode) {
    return hasContext || surfaceKind === "reader" ? "Talk freely about this" : "Talk to Toofi freely";
  }
  switch (mode) {
    case "learn":
      return hasContext || surfaceKind === "reader" ? "Explain or simplify this" : "Teach me this";
    case "practice":
      return hasContext || surfaceKind === "reader" ? "Quiz me on this" : "Give me practice";
    case "review":
      return "What should I revise?";
    case "plan":
      return "Build or fix my plan";
    case "create":
      return hasContext || surfaceKind === "reader" ? "Turn this into notes" : "Create study notes";
    default:
      return "Ask Toofi anything";
  }
};

export const getToofiHomePrompt = ({
  mode,
  surfaceKind,
  hasContext,
  stepByStep = false,
}: {
  mode: ToofiProductMode | null;
  surfaceKind: "reader" | "workspace";
  hasContext?: boolean;
  stepByStep?: boolean;
}) => {
  if (stepByStep) return "Teach this step by step";
  if (!mode) {
    if (hasContext) return surfaceKind === "workspace" ? "Ask about this" : "Ask about this file";
    return surfaceKind === "workspace" ? "Ask about your study" : "Ask about this file";
  }
  switch (mode) {
    case "learn":
      return hasContext
        ? "Explain this clearly"
        : surfaceKind === "reader"
          ? "Explain this file clearly"
          : "Explain a topic clearly";
    case "practice":
      return hasContext
        ? "Quiz me on this"
        : surfaceKind === "reader"
          ? "Quiz me on this file"
          : "Quiz me on a topic";
    case "review":
      return hasContext || surfaceKind === "reader" ? "What should I revise here?" : "What should I revise?";
    case "plan":
      return hasContext || surfaceKind === "reader" ? "Plan how I should study this" : "Plan my study";
    case "create":
      return hasContext || surfaceKind === "reader" ? "Turn this into notes" : "Create study notes";
    default:
      return surfaceKind === "workspace" ? "Ask about your study" : "Ask about this file";
  }
};
